/**
 * Per-frame content derivation (WI #5531).
 *
 * The content layer has state shared across several painters: the set of
 * activities near the viewport, and the resolved label-vs-banner decision. This
 * is computed once here (before any content painter runs) so labels and banners
 * agree, and so the expensive on-canvas banner-room measurement happens a single
 * time per frame. The result is stashed on `scene.content`.
 */
import { planBanners } from './BannerPainter';
import type { SceneContext } from '../../engine/SceneContext';
import type { ContentFrame } from '../../engine/types';

export function computeContentFrame(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): ContentFrame {
  const { map, io } = scene;
  // Fully-hidden activities are excluded here (never drawn): hidden phase/
  // workstream (`isHiddenOnMap`), the hide-mirrored filter
  // (`isHiddenMirroredActivity`) and filter hide-mode (WI #5596,
  // `isFilterHiddenActivityId` — non-matching activities while the hide
  // toggle is on). Filter *dimming* (opacity 0.2 for items that don't match
  // an active filter) is NOT a removal — each painter applies it via
  // `isShadowedActivityId`.
  const activities = io.activities.filter(
    (a) =>
      !a.isHiddenOnMap &&
      !map.isHiddenMirroredActivity(a.id) &&
      !map.isFilterHiddenActivityId(a.id),
  );
  const visible = activities.filter((a) => {
    const x = scene.screenX(map.activityPos(a));

    return x >= -100 && x <= scene.width + 200;
  });

  // Decide label-vs-banner for every activity in one pass (a banner suppresses
  // its label).
  const banners = planBanners(ctx, scene, visible);
  const bannerIds = new Set(banners.map((b) => b.activity.id));

  return { activities, visible, banners, bannerIds };
}
